import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import CapsuleTab from '@repo/ui/capsule-tab'
import {
  CheckMarkIcon,
  DeleteIcon,
  InsuranceIcon,
  PlusIcon,
  SendIcon,
  XIcon
} from '@repo/ui/icons'
import InnerBox, { InnerBoxTitle } from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { acceptProposition } from '../../../../api/enrollments/accept-proposition'
import { addCseToEnrollment } from '../../../../api/enrollments/add-cse'
import { getCseRegistration } from '../../../../api/enrollments/add-cse-registration'
import { createEnrollmentProposition } from '../../../../api/enrollments/create-enrollment-proposition'
import { deleteProposition } from '../../../../api/enrollments/delete-proposition'
import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { getEnrollmentFriends } from '../../../../api/enrollments/get-enrollment-friends'
import { getEnrollmentPropositions } from '../../../../api/enrollments/get-enrollment-propostions'
import { getMainProposition } from '../../../../api/enrollments/get-main-enrollment-propostion'
import { doesCseApplyToProposition } from '../../../../api/enrollments/is-cse-registration-related-to-proposition'
import { removeCseFromEnrollment } from '../../../../api/enrollments/remove-cse'
import { sendEnrollmentPropositionsEmail } from '../../../../api/enrollments/send-quota'
import { updateEnrollment } from '../../../../api/enrollments/update-enrollment'
import { ObserverKey, useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { ApplicationStatusEnum } from '../../../../lib/types/enums/enrollment/application-status.enum'
import { enrollmentEmailFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { UpdateInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { CseCodeForm } from '../../cse/cse-code-form'
import EnrollmentModificationNotificationModal from '../enrollment-modification-notification-modal'
import EnrollmentPropositionFormModal from '../enrolment-create-proposition-steps/enrollment-proposition-form-modal'
import {
  getParentEmailBasedOnPriority,
  mapEnrollmentPropositionInputToRequest
} from '../utils/enrollments-utils'
import EnrollmentPropositionDetails, {
  EnrollmentPropositionDetailsSkeleton
} from './enrollment-proposition-details'
import { SendEnrollmentProposalForm } from './send-enrollment-proposal-form'

type Props = {
  enrollmentId: number
  onSuccess?: () => void
}

const notifyKeys: ObserverKey[] = [
  'enrollment-status',
  'enrollment-profile',
  'enrollment-form-update',
  'enrollment-payment-record',
  'enrollment-profile-monitoring-payment-record',
  'enrollment-overpayment',
  'enrollment-proposition-summary',
  'enrollment-proposition-comparison'
]

const EnrollmentPropositionManager = ({ enrollmentId, onSuccess }: Props) => {
  const [activeTab, setActiveTab] = useState(0)
  const [index, setIndex] = useState(0)
  const observer = useObserver()
  const queryClient = useQueryClient()
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    | 'create-journey'
    | 'delete-proposition'
    | 'accept-proposition'
    | 'send-proposition'
    | 'add-cse'
    | 'remove-cse'
    | 'modification-notification',
    number
  >([
    'create-journey',
    'delete-proposition',
    'accept-proposition',
    'send-proposition',
    'add-cse',
    'remove-cse',
    'modification-notification'
  ])

  const { data: friends, refetch: refetchFriends } = useQuery({
    queryKey: ['enrollments-friends', enrollmentId],
    queryFn: () => getEnrollmentFriends(enrollmentId),
    enabled: !!enrollmentId
  })

  const { mutateAsync: sendEnrollmentQuotaMutation } = useMutation({
    mutationFn: sendEnrollmentPropositionsEmail,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('La proposition a été envoyée avec succès')
      closeModel('send-proposition')
    }
  })

  const { data: enrollment } = useQuery({
    queryKey: ['enrollment', enrollmentId, enrollmentEmailFields],
    queryFn: () => getEnrollment(enrollmentId, enrollmentEmailFields)
  })

  const parentEmail = enrollment ? getParentEmailBasedOnPriority(enrollment) : undefined
  const defaultEmails: string[] = parentEmail ? [parentEmail] : []

  const {
    data: propositions,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['propositions', enrollmentId],
    queryFn: () => getEnrollmentPropositions(enrollmentId)
  })

  const { data: mainProposition } = useQuery({
    queryKey: ['main-proposition', enrollmentId, activeTab],
    queryFn: () => getMainProposition(enrollmentId)
  })

  const sortedPropositions = useMemo(
    () =>
      [...(propositions?.data ?? [])].sort((p1, p2) =>
        p1.isMain ? -1 : p2.isMain ? 1 : p1.id - p2.id
      ),
    [propositions?.data]
  )

  useEffect(() => {
    if (!sortedPropositions.length) return

    if (activeTab > sortedPropositions.length - 1) setActiveTab(sortedPropositions.length - 1)
  }, [activeTab, sortedPropositions.length])

  const activeProposition = sortedPropositions[activeTab]

  const { data: cseRegistration, refetch: refetchCseRegistration } = useQuery({
    queryKey: ['cse-registration', enrollmentId],
    queryFn: () => getCseRegistration(enrollmentId)
  })

  const { data: isCseRelated, refetch: refetchCseRelated } = useQuery({
    queryKey: ['is-cse-registration-related', enrollmentId, activeProposition?.id],
    queryFn: () => doesCseApplyToProposition(enrollmentId, activeProposition?.id ?? 0),
    enabled: !!activeProposition?.id
  })

  const { mutateAsync: addCseMutation } = useMutation({
    mutationFn: addCseToEnrollment,
    onError: showAxiosError,
    onSuccess: async () => {
      closeModel('add-cse')
      await refetchCseRegistration()
      await refetchCseRelated()

      await observer.notify(notifyKeys)

      toast.success('Le code a été ajouté avec succès')
    }
  })

  const { mutateAsync: removeCseFromEnrollmentMutation } = useMutation({
    mutationFn: removeCseFromEnrollment,
    onError: showAxiosError,
    onSuccess: async () => {
      closeModel('remove-cse')
      queryClient.setQueryData(['cse-registration', enrollmentId], null)
      await refetchCseRegistration()
      await refetchCseRelated()

      await observer.notify(notifyKeys)

      toast.success('Le code a été supprimé avec succès')
    }
  })

  const { mutateAsync: createEnrollmentPropositionMutation, isPending: isCreatePending } =
    useMutation({
      mutationFn: createEnrollmentProposition,
      onError: showAxiosError,
      onSuccess: async () => {
        closeModel('create-journey')
        const { data } = await refetch()
        if (data) setActiveTab(data.data.length - 1)
      }
    })

  const { mutateAsync: updateEnrollmentMutation, isPending: isUpdatePending } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
    onError: showAxiosError,
    onSuccess: () => {
      refetchFriends()
    }
  })

  const { mutateAsync: deletePropositionMutation, isPending: isDeleteLoading } = useMutation({
    mutationFn: deleteProposition,
    onSuccess: () => {
      refetch().then(() => {
        setActiveTab((prev) => prev - 1)
      })
      closeModel('delete-proposition')
    }
  })

  const { mutateAsync: acceptPropositionMutation, isPending: isAcceptLoading } = useMutation({
    mutationFn: acceptProposition,
    onSuccess: () => {
      refetch().then(() => {
        setActiveTab(0)
        observer.notify(['enrollment-proposition-comparison'])
      })
      closeModel('accept-proposition')
      // selecting another stay changes a confirmed stay: ask whether to notify
      if (
        enrollment?.status === ApplicationStatusEnum.CONFIRMED ||
        enrollment?.status === ApplicationStatusEnum.AFFECTED
      )
        toggleModal('modification-notification')
    }
  })

  const handlePropositionSubmit = async (values: EnrollmentPropositionInput) => {
    const { declaredFriends, ...input } = values

    try {
      // the proposition first: updating the enrollment requires a main proposition
      await createEnrollmentPropositionMutation({
        ...mapEnrollmentPropositionInputToRequest(input),
        id: enrollmentId
      })
    } catch {
      // the failing mutation already shows its error
      return
    }
    // update the enrollment friends wishlist (its failure already shows its error)
    await updateEnrollmentMutation({ id: enrollmentId, declaredFriends }).catch(() => undefined)

    await refetchCseRegistration()
    await refetchCseRelated()

    closeModel('create-journey')
  }

  if (isLoading || !propositions) return <EnrollmentPropositionDetailsSkeleton />

  return (
    <InnerBox
      id={'enrollment-trips'}
      titleColor={tailwindColors.black.DEFAULT}
      className={'p-5 rounded-2xl'}
    >
      <CapsuleTab
        variant="secondary"
        tabs={sortedPropositions.map((proposition, index) => ({
          label: proposition.isMain ? 'Séjour sélectionné' : 'Proposition',
          badgeNumber: proposition.isMain ? undefined : NumberFormatter.numberFormatter(index)
        }))}
        activeTab={activeTab}
        outline
        onTabChange={setActiveTab}
        customTabComponent={
          <div className={'flex items-center gap-2'}>
            <Button
              type={'button'}
              variant={'outline'}
              className={'whitespace-nowrap'}
              label={'Envoyer notre proposition'}
              icon={SendIcon}
              onClick={() => {
                if (activeProposition) toggleModal('send-proposition', activeProposition.id)
              }}
            />
            <Button
              type={'button'}
              variant="ghost"
              icon={PlusIcon}
              className={'py-2 px-3'}
              onClick={() => {
                setIndex(0)
                toggleModal('create-journey')
              }}
            />
          </div>
        }
      />
      {activeProposition && (
        <div>
          <div className="h-4" />
          <div className="flex items-center justify-between">
            <div className="flex flex-row gap-2 items-center">
              <InnerBoxTitle
                title={'Séjour'}
                subTitle={'Accéder au site'}
                titleColor={tailwindColors.black.DEFAULT}
                titleVariant="sub-heading"
              />

              {!cseRegistration?.id && (
                <Button
                  icon={PlusIcon}
                  variant="ghost"
                  label="Ajouter un code"
                  onClick={() => toggleModal('add-cse')}
                />
              )}

              {cseRegistration?.id && (
                <Button
                  icon={() => <XIcon color={'red'} width={'20'} height={'20'} />}
                  iconToTheRight
                  className={cn(
                    'border border-grey-light text-grey',
                    enrollment?.confirmedAt
                      ? 'bg-white hover:bg-white'
                      : 'bg-white-hover hover:bg-white-light-hover'
                  )}
                  variant="primary"
                  label={`CSE : ${cseRegistration.cse.label}`}
                  disabled={!!enrollment?.confirmedAt}
                  onClick={() => toggleModal('remove-cse')}
                />
              )}
            </div>

            {!activeProposition.isMain && (
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  variant="destructive"
                  label="Supprimer cette proposition"
                  onClick={() => toggleModal('delete-proposition', activeProposition.id)}
                />

                <Button
                  type="button"
                  variant="ghost"
                  label="Sélectionner ce séjour"
                  onClick={() => toggleModal('accept-proposition', activeProposition.id)}
                />
              </div>
            )}
          </div>
          <EnrollmentPropositionDetails
            mode={'instant'}
            enrollmentId={enrollmentId}
            propositionId={activeProposition.id}
            onSuccess={async () => {
              await refetchFriends()
              await refetchCseRegistration()
              await refetchCseRelated()
              await observer.notify(notifyKeys)

              onSuccess?.()
            }}
            mainProposition={conditionalRender(
              mainProposition?.id !== activeProposition.id,
              mainProposition
            )}
          />
        </div>
      )}
      <EnrollmentPropositionFormModal
        defaultIndex={index}
        isOpen={modalState['create-journey']}
        onClose={() => closeModel('create-journey')}
        isLoading={isCreatePending || isUpdatePending}
        defaultValues={{
          declaredFriends: friends?.data ?? [],
          cseCode: cseRegistration?.cse?.code
        }}
        handleSubmit={handlePropositionSubmit}
      />
      <Modal
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: async () => {
            if (selectedData)
              await deletePropositionMutation({ enrollmentId, propositionId: selectedData })
            closeModel('delete-proposition')
          }
        }}
        isLoading={isDeleteLoading}
        onCancel={() => toggleModal('delete-proposition')}
        isOpen={modalState['delete-proposition']}
        title={'Supprimer cette proposition'}
        icon={DeleteIcon}
        variant={'destructive'}
      />

      <Modal
        actionButtonsOptions={{
          confirmText: 'Confirmer',
          cancelText: 'Fermer',
          onConfirm: async () => {
            if (selectedData)
              await acceptPropositionMutation({ enrollmentId, propositionId: selectedData })

            closeModel('accept-proposition')
          }
        }}
        isLoading={isAcceptLoading}
        onCancel={() => toggleModal('accept-proposition')}
        isOpen={modalState['accept-proposition']}
        title={'Confirmez-vous la sélection de ce séjour ?'}
        icon={CheckMarkIcon}
        variant={'confirmation'}
      />

      <Modal
        isOpen={modalState['send-proposition']}
        title={`Envoyer notre proposition`}
        icon={InsuranceIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('send-proposition')}
      >
        {selectedData && enrollment && (
          <SendEnrollmentProposalForm
            mode="enrollment"
            enrollmentId={enrollmentId}
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{ emails: defaultEmails }}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-proposition'),
              onConfirm: ({ emails }) => sendEnrollmentQuotaMutation({ id: enrollmentId, emails })
            })}
          />
        )}
      </Modal>

      <Modal
        isOpen={modalState['add-cse']}
        title={'Ajouter un code'}
        icon={PlusIcon}
        hideActions
        size="small"
        onCancel={() => closeModel('add-cse')}
      >
        <CseCodeForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ enrollmentId }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('add-cse'),
            onConfirm: async (data) => await addCseMutation(data)
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['remove-cse']}
        title={'Supprimer un code'}
        icon={DeleteIcon}
        variant="destructive"
        size="small"
        onCancel={() => closeModel('remove-cse')}
        hasBorderBottom={false}
        actionButtonsOptions={{
          cancelText: 'Annuler',
          confirmText: 'Supprimer',
          onConfirm: async () => await removeCseFromEnrollmentMutation(enrollmentId)
        }}
      >
        {!isCseRelated && (
          <div className={cn(MODAL_BODY_CLASSNAME, 'justify-center items-center flex')}>
            <Badge
              text={'Le séjour choisi ne fait partie de la sélection de ce CSE'}
              variant="danger"
            />
          </div>
        )}
      </Modal>

      <EnrollmentModificationNotificationModal
        enrollmentId={enrollmentId}
        isOpen={modalState['modification-notification']}
        closeModal={() => closeModel('modification-notification')}
      />
    </InnerBox>
  )
}

export default EnrollmentPropositionManager
